import { useEffect, useState } from 'react';
import { ThemeProvider } from '@mui/material';
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { createDefaultTheme } from 'theme';
import { DataContextProvider } from 'context/data/dataContext';
import { MobileContextProvider } from './context/mobile/mobileContext';
import { Mobile } from './features/Mobile/Mobile';
import { AdminPage } from 'features/Admin/Admin';
import { ProgressIndicator } from 'components/ProgressIndicator/ProgressIndicator';
import rtl from 'jss-rtl';
import { jssPreset, StylesProvider } from '@mui/styles';
import { create } from 'jss';

import 'App.css';
import { LocalizedApp } from 'locales';
import { FormattedMessage } from 'react-intl';
import TagManager from 'react-gtm-module';
import { UserContextProvider, useUserContext } from "context/data/userContext";

const isRTL = /^(he|ar)\b/.test(navigator.language);
const jss = create({ plugins: [...jssPreset().plugins, rtl()] });

interface AuthenticatedViewProps {
  authCondition: (user: any) => boolean,
  children: (user: any) => JSX.Element
};

function AuthenticatedView({ authCondition, children }: AuthenticatedViewProps) {
  const { isLoading, user } = useUserContext();

  useEffect(() => {
    if (!user) {
      return;
    }
    TagManager.dataLayer({
      dataLayer: {
        'yumId': user.yumId
      }
    });
  }, [user]);

  if (isLoading) {
    return (
      <ProgressIndicator />
    )
  }

  if (!authCondition(user)) {
    return (
      <h1><FormattedMessage id="app.access.denied" key={`app.access.denied`} /></h1>
    )
  }

  return children(user);
}

function App() {
  const [isMobileApp] = useState(!!(window as any)?.mapp);

  return (
    <ThemeProvider theme={createDefaultTheme(isRTL)}>
      <StylesProvider jss={jss}>
        <div dir={isRTL ? 'rtl' : 'ltr'}>
          <DataContextProvider>
            <UserContextProvider>
              <LocalizedApp>
                <Router>
                  <Routes>
                    <Route path={isMobileApp ? '/*' : '/mobile/*'} element={
                      <AuthenticatedView authCondition={(user) => user.roles.includes('user')}>
                        {(user) => (
                          <MobileContextProvider>
                            <Mobile user={user}/>
                          </MobileContextProvider>
                        )}
                      </AuthenticatedView>
                    }/>
                    {!isMobileApp && <Route path="/*" element={
                      <AuthenticatedView
                        authCondition={(user) => user.roles.includes('global-admin') || user.roles.includes('country-admin')}>
                        {(user) => (
                          <AdminPage user={user}/>
                        )}
                      </AuthenticatedView>
                    }/>}
                  </Routes>
                </Router>
              </LocalizedApp>
            </UserContextProvider>
          </DataContextProvider>
        </div>
      </StylesProvider>
    </ThemeProvider>
  );
}

export default App;
